import moment from 'moment'
import {useQuery} from '@apollo/client'
import {Link} from 'react-router-dom'
import {gql} from '@apollo/client'
import {FormattedMessage, useIntl} from 'react-intl'
import {LeadByID} from '../leadTypes'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {parseId} from '../../../helpers'

const colon = ':'
const arrowRight = '→'
const space = ' '

const GET_QUOTE_BY_LEAD = gql`
  query getQuoteByLead($lead: ID) {
    allQuotes(lead: $lead, first: 1) {
      edges {
        node {
          id
          planName
          status
        }
      }
    }
  }
`

interface LeadSummaryPanelProps {
  lead: LeadByID
  title?: string
}

export const LeadSummaryPanel: React.FC<LeadSummaryPanelProps> = ({lead, title}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const resolvedTitle =
    title ?? intl.formatMessage({id: 'leads.list.summary.title', defaultMessage: 'Lead summary'})
  const leadPk = parseId(lead.id)
  const {data: quoteData} = useQuery(GET_QUOTE_BY_LEAD, {
    variables: {lead: leadPk},
    skip: !leadPk,
    fetchPolicy: 'cache-first',
  })

  const quoteNode = quoteData?.allQuotes?.edges?.[0]?.node
  const quoteId = quoteNode?.id

  const assignedTo =
    lead.user?.edges?.length > 0
      ? lead.user.edges.map(({node}) => `${node.firstName} ${node.lastName}`).join(', ')
      : na
  const tags =
    lead.tagInsurance?.edges?.length > 0
      ? lead.tagInsurance.edges.map(({node}) => node.name).join(', ')
      : na
  const createdAt = lead.created ? moment(lead.created).format('DD MMM YYYY, hh:mm A') : na
  const lastUpdate = lead.updated ? moment(lead.updated).format('DD MMM YYYY, hh:mm A') : na
  const addressLine =
    lead.personalLead?.streetAddress || lead.comercialLead?.contactLastname || na
  const city = lead.personalLead?.city || na
  const state = lead.personalLead?.state || na
  const postalCode = lead.personalLead?.postalCode || na
  const birthDay = lead.personalLead?.birthDay
    ? moment(lead.personalLead.birthDay).format('DD MMM, YY')
    : na

  return (
    <div>
      <div className='kt-heading kt-heading--sm custom-border-brand my-3'>{resolvedTitle}</div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.pipeline' defaultMessage='Pipeline' />{colon}
        </span>
        <span className='kt-widget__data'>{lead.stagePipeline?.pipeline?.name || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.stage' defaultMessage='Stage' />{colon}
        </span>
        <span className='kt-widget__data d-flex align-items-center gap-2'>
          {lead.stagePipeline?.name || na}
          {quoteId && (
            <Link
              to={`/one-quote/${quoteId}`}
              className='btn btn-sm btn-light-primary py-1 px-2 ms-2'
              style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}}
              title={intl.formatMessage({
                id: 'leads.list.summary.openQuoteTooltip',
                defaultMessage: 'Open associated quote',
              })}
            >
              <FormattedMessage id='leads.list.summary.viewQuote' defaultMessage='View quote' />
              {space}
              {arrowRight}
            </Link>
          )}
        </span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.source' defaultMessage='Source' />{colon}
        </span>
        <span className='kt-widget__data'>{lead.leadSource?.name || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.tags' defaultMessage='Tags' />{colon}
        </span>
        <span className='kt-widget__data'>{tags}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.reasonForLoss' defaultMessage='Reason for loss' />{colon}
        </span>
        <span className='kt-widget__data'>{lead.reasonLeadLoss?.reason || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.assignedTo' defaultMessage='Assigned to' />{colon}
        </span>
        <span className='kt-widget__data'>{assignedTo}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.createdAt' defaultMessage='Created at' />{colon}
        </span>
        <span className='kt-widget__data'>{createdAt}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.lastUpdate' defaultMessage='Last update' />{colon}
        </span>
        <span className='kt-widget__data'>{lastUpdate}</span>
      </div>
      <div className='kt-heading kt-heading--sm custom-border-brand my-3'>
        <FormattedMessage id='leads.list.summary.personalInfo' defaultMessage='Personal information' />
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.name' defaultMessage='Name' />{colon}
        </span>
        <span className='kt-widget__data'>{lead.name || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.email' defaultMessage='Email' />{colon}
        </span>
        <span className='kt-widget__data'>{lead.email || na}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.phone' defaultMessage='Phone' />{colon}
        </span>
        <div className='kt-widget__data'>
          <PhoneProviderActions phone={lead.phone} />
        </div>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.dateOfBirth' defaultMessage='Date of Birth' />{colon}
        </span>
        <span className='kt-widget__data'>{birthDay}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='leads.list.summary.address' defaultMessage='Address' />{colon}
        </span>
        <span className='kt-widget__data'>{addressLine}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.city' defaultMessage='City' />{colon}
        </span>
        <span className='kt-widget__data'>{city}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.state' defaultMessage='State' />{colon}
        </span>
        <span className='kt-widget__data'>{state}</span>
      </div>
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          <FormattedMessage id='common.field.zipCode' defaultMessage='Zip code' />{colon}
        </span>
        <span className='kt-widget__data'>{postalCode}</span>
      </div>
    </div>
  )
}
